import React, { useEffect, useState } from 'react';
import { Route, Routes } from 'react-router';
import { BrowserRouter } from 'react-router-dom';
import { createTheme, INavLink, initializeIcons, Theme, ThemeProvider } from '@fluentui/react';

import PublicLayout from './layouts/public/public.layout';
import PrivateLayout from './layouts/private/private.layout';

import LoginPage from './pages/login/login.page';
import RegisterProfessionalPage from './pages/register-professional/register-professional.page';
import RegisterConfirmPage from './pages/register-confirm/register-confirm.page';
import RecoverPasswordPage from './pages/recover-password/recover-password.page';
import RecoverPasswordConfirmPage from './pages/recover-password-confirm/recover-password-confirm.page';
import DashboardPage from './pages/dashboard/dashboard.page';
import WorkOrdersPage from './pages/work-orders/work-orders.page';
import ChronologyPage from './pages/chronology/chronology.page';
import ChronologyReportPage from './pages/chronology-report/chronology-report.page';
import ChronologyActualSituationReportPage from './pages/chronology-actual-situation-report/chronology-actual-situation-report.page';
import ChronologyOrdersReportPage from './pages/chronology-orders-report/chronology-orders-report.page';
import TokenExecutePage from './pages/token-execute/token-execute.page';
import RegisterInvitationPage from './pages/register-invitation/register-invitation.page';
import DynamicFormsPage from './pages/dynamic-forms/dynamic-forms.page';
import WelcomePage from './pages/welcome/welcome.page';
import RouteReportPage from './pages/route-report/route-report.page';
import WorkOrdersReadonlyPage from './pages/work-orders-readonly/work-orders-readonly.page';
import WaypointsReportPage from './pages/waypoints-report/waypoints-report.page';

import useAuthStatus from './hooks/auth/AuthHook';

import ItemUsuario from './models/ItemUsuario';
import ItemPermiso from './models/ItemPermiso';
import ElementEnum from './enums/ElementEnum';

import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

const theme : Theme = createTheme({
  defaultFontStyle: {
    fontFamily: 'Figtree',
  },
});

initializeIcons();

const App = () => {
  const auth = useAuthStatus();

  const [menuItems, setMenuItems] = useState<INavLink[]>([]);

  useEffect(() => {
    if (auth.user) {
      let items : INavLink[] = [];
      if (auth.user.UsuarioAdmin) {
        items.push(
          {
            name: '',
            url: "/main/resumen-actividad",
            key: "0",
            title: "Inicio",
            icon: "AccountActivity",
          },
        );
      }
      if (auth.user.Permisos.find((p: ItemPermiso) => p.IdElemento === ElementEnum.WORK_ORDERS)?.Ver) {
        items.push(
          {
            name: '',
            url: "/main/ordenes-trabajo",
            key: "1000",
            title: "Bandeja de entrada",
            icon: "Inbox",
          }
        );
        if (auth.user.UsuarioAdmin) {
          items.push(
            {
              name: '',
              url: "/main/cronologia",
              key: "1001",
              title: "Cronología",
              icon: "Clock",
            },
          );
        }
      }
      // if (auth.user.Permisos.find((p: ItemPermiso) => p.IdElemento === ElementEnum.MESSAGING)?.Ver) {
      //   items.push(
      //     {
      //       name: '',
      //       url: "/main/mensajeria",
      //       key: "1002",
      //       title: "Mensajeria",
      //       icon: "Message",
      //     },
      //   );
      // }
      // if (auth.user.Permisos.find((p: ItemPermiso) => p.IdElemento === ElementEnum.REPORTING)?.Ver) {
      //   items.push(
      //     {
      //       name: '',
      //       url: "/main/reporting",
      //       key: "1003",
      //       title: "Reporting",
      //       icon: "ReportDocument",
      //     },
      //   );
      // }
      // if (auth.user.Permisos.find((p: ItemPermiso) => p.IdElemento === ElementEnum.FORMS)?.Ver) {
      //   items.push(
      //     {
      //       name: '',
      //       url: "/main/formularios-dinamicos",
      //       key: "1004",
      //       title: "Formularios dinámicos",
      //       icon: "OfficeFormsLogo",
      //     },
      //   );
      // }
      // if (auth.user.Permisos.find((p: ItemPermiso) => p.IdElemento === ElementEnum.JOURNAL_REGISTRY)?.Ver) {
      //   items.push(
      //     {
      //       name: '',
      //       url: "/main/registro-jornada",
      //       key: "1005",
      //       title: "Registro de jornada",
      //       icon: "WorkforceManagement",
      //     },
      //   );
      // }
      setMenuItems(items);
    }
    else {
      setMenuItems([]);
    }
  }, [auth.user]);

  const onLogin = (user: ItemUsuario) : void => {
    auth.setUser(user);
  };
  
  return (
    <ThemeProvider 
      theme={theme} 
      className="h-100 overflow-hidden">
      <BrowserRouter>
        <Routes>
          <Route path='/' element={
            <PublicLayout 
              showRegistry={true}
              auth={auth}>
              <LoginPage onLogin={onLogin} />
            </PublicLayout>
          } />
          <Route path="/registro-profesional" element={
            <PublicLayout 
              showRegistry={false}
              auth={auth}>
              <RegisterProfessionalPage />
            </PublicLayout>
          } />
          <Route path="/registro-confirmar" element={
            <PublicLayout 
              showRegistry={false}
              auth={auth}>
              <RegisterConfirmPage />
            </PublicLayout>
          } />
          <Route path="/registro-invitacion" element={
            <PublicLayout 
              showRegistry={false}
              auth={auth}>
              <RegisterInvitationPage />
            </PublicLayout>
          } />
          <Route path="/recuperar" element={
            <PublicLayout 
              showRegistry={true}
              auth={auth}>
              <RecoverPasswordPage />
            </PublicLayout>
          } />
          <Route path="/recuperar-confirmar" element={
            <PublicLayout 
              showRegistry={true} 
              auth={auth}>
              <RecoverPasswordConfirmPage />
            </PublicLayout>
          } />
          <Route path="/main/bienvenida" element={
            <PrivateLayout 
              auth={auth}
              onLogout={auth.logout}
              menuItems={menuItems}>
              <WelcomePage />
            </PrivateLayout>
          } />
          <Route path="/main/resumen-actividad" element={
            <PrivateLayout 
              auth={auth}
              onLogout={auth.logout}
              menuItems={menuItems}>
              <DashboardPage />
            </PrivateLayout>
          } />
          <Route path="/main/ordenes-trabajo" element={
            <PrivateLayout 
              auth={auth}
              onLogout={auth.logout}
              menuItems={menuItems}>
              {
                (auth.user && auth.user.UsuarioAdmin) ? (
                  <WorkOrdersPage />
                ) : (
                  <WorkOrdersReadonlyPage />
                )
              }
            </PrivateLayout>
          } />
          <Route path="/main/cronologia" element={
            <PrivateLayout 
              auth={auth}
              onLogout={auth.logout}
              menuItems={menuItems}>
              <ChronologyPage />
            </PrivateLayout>
          } />
          <Route path="/main/formularios-dinamicos" element={
            <PrivateLayout 
              auth={auth}
              onLogout={auth.logout}
              menuItems={menuItems}>
              <DynamicFormsPage />
            </PrivateLayout>
          } />
          <Route path="/main/cronologia/reporte" element={
            <ChronologyReportPage />
          } />
          <Route path="/main/cronologia/situacion-actual" element={
            <ChronologyActualSituationReportPage />
          } />
          <Route path="/main/cronologia/orden" element={
            <ChronologyOrdersReportPage />
          } />
          <Route path="/main/token/ejecutar" element={
            <TokenExecutePage />
          } />
          <Route path="/main/rutas/reporte" element={
            <RouteReportPage />
          } />
          <Route path="/main/waypoint/reporte" element={
            <WaypointsReportPage />
          } />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
